import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
  Filters,
  PopularFilterOption,
  TimeFilter,
} from "../../constants/types";
import { OptionProps } from "../../constants/types";
import { useGlobalState } from "../../services/context/AuthContext";

interface FlightListFilterCardProps {
  initialFilters: Filters;
  airlines: PopularFilterOption[];
  origin?: string;
  destination?: string;
  onFilterChange?: (filters: Filters) => void;
}

const FlightListFilterCard: React.FC<FlightListFilterCardProps> = ({
  initialFilters,
  airlines, // Available airlines
  origin, // Departure city
  destination, // Arrival city
  onFilterChange = () => {}, // Callback function to send updated filters
}) => {
  // State for all filters
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const {  userInfo} = useGlobalState()

  // Update local state if props change
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  const handleCheckboxChange = <T extends keyof Filters>(
    group: T,
    key: keyof Filters[T]
  ): void => {
    setFilters((prevFilters) => {
      const updatedGroup = {
        ...prevFilters[group],
        [key]: !prevFilters[group][key as keyof (typeof prevFilters)[T]], // Type-safe access
      };

      const newFilters = {
        ...prevFilters,
        [group]: updatedGroup,
      };

      onFilterChange(newFilters);
      return newFilters;
    });
  };

  // Handle price range change
  const handlePriceRangeChange = (
    _event: Event,
    newValue: number | number[]
  ): void => {
    const newFilters = {
      ...filters,
      priceRange: {
        ...filters.priceRange,
        value: newValue as [number, number],
      },
    };

    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  // Handle time of day selection
  const handleTimeSelection = (
    group: "departureTime" | "arrivalTime",
    key: keyof TimeFilter
  ): void => {
    setFilters((prevFilters) => {
      const isCurrentlySelected = prevFilters[group][key];

      // Reset all to false
      const newTimeValues: TimeFilter = {
        before6AM: false,
        from6AMto12PM: false,
        from12PMto6PM: false,
        after6PM: false,
      };

      // Toggle the selected one (if it was true, make it false)
      if (!isCurrentlySelected) {
        newTimeValues[key] = true;
      }

      const newFilters = {
        ...prevFilters,
        [group]: newTimeValues,
      };

      onFilterChange(newFilters);
      return newFilters;
    });
  };

  // Format price for display
  const formatPrice = (value: number): string => {
    return `${userInfo?.currencyCode} ${value.toLocaleString()}`;
  };

  return (
    <div>
      <div className="card ms-3 mt-3">
        <div className="card-header">
          <h3 className="card-title">Filter</h3>
          <FilterAltIcon />
        </div>
        <div className="card-body">
          <div className="grid gap-y-3.5">
            <div className="filter-section">
              {/* Popular Filters */}
              <div className="filter-group coupons-list">
                <h3>Popular Filters</h3>
                {airlines?.map((option: any) => (
                  <FormControlLabel
                    key={option.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.airlines[option.key] || false}
                        onChange={() =>
                          handleCheckboxChange("airlines", option.key)
                        }
                      />
                    }
                    label={option.label}
                  />
                ))}
              </div>

              {/* Price Range */}
              <div className="filter-group">
                <h3>Price Range</h3>
                <div className="price-range">
                  <Box>
                    <Slider
                      size="small"
                      value={filters.priceRange.value}
                      min={filters.priceRange.min}
                      max={filters.priceRange.max}
                      onChange={handlePriceRangeChange}
                      valueLabelDisplay="auto"
                      valueLabelFormat={formatPrice}
                    />
                  </Box>
                  <span>
                    {formatPrice(filters.priceRange.value[0])} -{" "}
                    {formatPrice(filters.priceRange.value[1])}
                  </span>
                </div>
              </div>

              {/* Stops */}
              <div className="filter-group">
                <h3>Stops</h3>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.stops.nonstop}
                      onChange={() => handleCheckboxChange("stops", "nonstop")}
                    />
                  }
                  label="Non-stops"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.stops.oneStop}
                      onChange={() => handleCheckboxChange("stops", "oneStop")}
                    />
                  }
                  label="1 Stop"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.stops.twoOrMoreStops}
                      onChange={() =>
                        handleCheckboxChange("stops", "twoOrMoreStops")
                      }
                    />
                  }
                  label="2+ Stop"
                />
              </div>

              {/* Departure Time */}
              <div className="filter-group">
                <h3>Departure from {origin}</h3>
                <div className="departure-time flight-list-font">
                  <div
                    className={filters.departureTime.before6AM ? "active" : ""}
                    onClick={() =>
                      handleTimeSelection("departureTime", "before6AM")
                    }
                  >
                    <img
                      src="media/images/before-6.png"
                      alt="before"
                      className=" ps-3"
                    />
                    Before 6 AM
                  </div>
                  <div
                    className={
                      filters.departureTime.from6AMto12PM ? "active" : ""
                    }
                    onClick={() =>
                      handleTimeSelection("departureTime", "from6AMto12PM")
                    }
                  >
                    <img
                      src="media/images/6-to-12-pm.png"
                      alt="before"
                      className=" ps-3"
                    />
                    6 AM - 12 PM
                  </div>
                  <div
                    className={
                      filters.departureTime.from12PMto6PM ? "active" : ""
                    }
                    onClick={() =>
                      handleTimeSelection("departureTime", "from12PMto6PM")
                    }
                  >
                    <img
                      src="media/images/12-to-6-pm.png"
                      alt="before"
                      className=" ps-3"
                    />
                    12 PM - 6 PM
                  </div>
                  <div
                    className={filters.departureTime.after6PM ? "active" : ""}
                    onClick={() =>
                      handleTimeSelection("departureTime", "after6PM")
                    }
                  >
                    <img
                      src="media/images/after-6.png"
                      alt="before"
                      className=" ps-3"
                    />
                    After 6 PM
                  </div>
                </div>
              </div>

              {/* Arrival Time */}
              <div className="filter-group">
                <h3>Arrival at {destination}</h3>
                <div className="departure-time flight-list-font">
                  <div
                    className={filters.arrivalTime.before6AM ? "active" : ""}
                    onClick={() =>
                      handleTimeSelection("arrivalTime", "before6AM")
                    }
                  >
                    <img
                      src="media/images/before-6.png"
                      alt="before"
                      className=" ps-3"
                    />
                    Before 6 AM
                  </div>
                  <div
                    className={
                      filters.arrivalTime.from6AMto12PM ? "active" : ""
                    }
                    onClick={() =>
                      handleTimeSelection("arrivalTime", "from6AMto12PM")
                    }
                  >
                    <img
                      src="media/images/6-to-12-pm.png"
                      alt="before"
                      className=" ps-3"
                    />
                    6 AM - 12 PM
                  </div>
                  <div
                    className={
                      filters.arrivalTime.from12PMto6PM ? "active" : ""
                    }
                    onClick={() =>
                      handleTimeSelection("arrivalTime", "from12PMto6PM")
                    }
                  >
                    <img
                      src="media/images/12-to-6-pm.png"
                      alt="before"
                      className=" ps-3"
                    />
                    12 PM - 6 PM
                  </div>
                  <div
                    className={filters.arrivalTime.after6PM ? "active" : ""}
                    onClick={() =>
                      handleTimeSelection("arrivalTime", "after6PM")
                    }
                  >
                    <img
                      src="media/images/after-6.png"
                      alt="before"
                      className=" ps-3"
                    />
                    After 6 PM
                  </div>
                </div>
              </div>

              {/* Flight Quality */}
              {/* <div className="filter-group">
                <h3>Flight Quality</h3>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.flightQuality.wifi}
                      onChange={() =>
                        handleCheckboxChange("flightQuality", "wifi")
                      }
                    />
                  }
                  label="Show Wi-Fi flights only"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.flightQuality.redEye}
                      onChange={() =>
                        handleCheckboxChange("flightQuality", "redEye")
                      }
                    />
                  }
                  label="Show Red-Eyes"
                />
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightListFilterCard;
